import { ApiService, ApiResponse } from './apiService';
import { getJwtFromCookie, getUserEmailFromJwt } from '../utils/auth';
import { 
  PropertyAnalysisRequest,
  PropertyAnalysisResponse,
  PropertyAnalysisError,
  PropertyServiceConfig,
  AnalysisToolType,
  PropertySource
} from '../types/property';

export class PropertyService {
  private apiService: ApiService;
  private config: PropertyServiceConfig;
  private activeRequests: Map<string, AbortController> = new Map();
  private requestInProgress: Set<string> = new Set();

  constructor(config?: Partial<PropertyServiceConfig>) {
    this.config = {
      baseUrl: 'https://property.streamio.ai/api/v1',
      timeout: 120000, // 2 minutes
      retryAttempts: 1, // Reduced from 2 to avoid overwhelming server
      retryDelay: 1000,
      ...config
    };

    this.apiService = new ApiService(this.config.baseUrl);
  }

  /**
   * Validates a property URL format
   */
  public validatePropertyUrl(url: string, source: PropertySource): boolean {
    const urlPattern = source === 'redfin' 
      ? /^https?:\/\/(www\.)?redfin\.com\/.*\/home\/\d+$/
      : /^https?:\/\/(www\.)?zillow\.com\/homedetails\/.*\/\d+_zpid\/$/;
    
    return urlPattern.test(url);
  }

  /**
   * Creates analysis configuration based on selected tools
   */
  private createAnalysisConfig(selectedTools: AnalysisToolType[]): PropertyAnalysisRequest['config'] {
    const includeAgents: PropertyAnalysisRequest['config']['includeAgents'] = {
      propertyParser: false,
      cma: false,
      neighborhood: false,
      pricing: false,
      negotiation: false,
      floorPlan: false,
      listingDescription: false,
      clientConsultation: false,
      leadQualification: false,
      transactionManagement: false,
      preTourPrep: false,
      clientMatch: false,
      buyerSentiment: false,
      agentOrchestrator: false,
    };

    // Map selected tools to API configuration
    selectedTools.forEach(tool => {
      switch (tool) {
        case 'propertyParser':
          includeAgents.propertyParser = true;
          break;
        case 'cma':
          includeAgents.cma = true;
          break;
        case 'neighborhood':
          includeAgents.neighborhood = true;
          break;
        case 'pricing':
          includeAgents.pricing = true;
          break;
        case 'negotiation':
          includeAgents.negotiation = true;
          break;
        case 'floorPlan':
          includeAgents.floorPlan = true;
          break;
        case 'listingDescription':
          includeAgents.listingDescription = true;
          break;
        case 'clientConsultation':
          includeAgents.clientConsultation = true;
          break;
        case 'leadQualification':
          includeAgents.leadQualification = true;
          break;
        case 'transactionManagement':
          includeAgents.transactionManagement = true;
          break;
        case 'preTourPrep':
          includeAgents.preTourPrep = true;
          break;
        case 'clientMatch':
          includeAgents.clientMatch = true;
          break;
        case 'buyerSentiment':
          includeAgents.buyerSentiment = true;
          break;
        case 'agentOrchestrator':
          includeAgents.agentOrchestrator = true;
          break;
        default:
          console.warn(`Unknown analysis tool: ${tool}`);
      }
    });

    return {
      includeAgents,
      timeout: this.config.timeout,
      retryAttempts: this.config.retryAttempts
    };
  }

  /**
   * Creates a delay for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  /**
   * Handles API errors and converts them to PropertyAnalysisError
   */
  private handleApiError(error: any, context: string): PropertyAnalysisError {
    if (error instanceof PropertyAnalysisError) {
      return error;
    }

    const propertyError = new PropertyAnalysisError(
      error.message || `Failed to ${context}`,
      error.code || 'UNKNOWN_ERROR'
    ) as PropertyAnalysisError;

    if (error.status) {
      propertyError.status = error.status;
    }

    if (error.details) {
      propertyError.details = error.details;
    }

    return propertyError;
  }

  /**
   * Makes HTTP request with retry logic and timeout handling
   */
  private async makeRequestWithRetry<T>(
    requestFn: () => Promise<ApiResponse<T>>,
    context: string,
    requestId?: string
  ): Promise<T> {
    let lastError: any;
    let attempt = 0;
    const maxAttempts = this.config.retryAttempts + 1;

    while (attempt < maxAttempts) {
      try {
        // Check if request was aborted
        if (requestId && this.activeRequests.has(requestId)) {
          const controller = this.activeRequests.get(requestId);
          if (controller?.signal.aborted) {
            throw new PropertyAnalysisError('Request was cancelled', 'REQUEST_CANCELLED');
          }
        }

        const response = await requestFn();
        
        if (response.success && response.data) {
          return response.data;
        } else {
          const error = new PropertyAnalysisError(
            response.error || 'API request failed',
            'API_ERROR'
          ) as PropertyAnalysisError;
          
          // Preserve HTTP status code for retry logic
          if (response.status) {
            error.status = response.status;
          }
          
          throw error;
        }
      } catch (error: any) {
        lastError = error;
        attempt++;

        // Don't retry on certain errors
        if (
          error.code === 'REQUEST_CANCELLED' ||
          error.status === 400 || // Bad request
          error.status === 401 || // Unauthorized
          error.status === 403 || // Forbidden
          error.status === 404    // Not found
        ) {
          break;
        }

        // For 500 errors (server issues), increase retry delay significantly
        // This might be rate limiting or server overload
        if (attempt < maxAttempts) {
          let delayMs = this.config.retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
          
          // For 500 errors, add additional delay to avoid overwhelming the server
          if (error.status === 500) {
            delayMs = Math.max(delayMs, 30000); // Minimum 30 seconds for 500 errors
            console.warn(`${context} attempt ${attempt} failed with HTTP 500 (server error), waiting ${delayMs}ms before retry...`, error);
          } else {
            console.warn(`${context} attempt ${attempt} failed, retrying in ${delayMs}ms...`, error);
          }
          
          await this.delay(delayMs);
        }
      }
    }

    throw this.handleApiError(lastError, context);
  }

  /**
   * Analyzes a property using the complete analysis endpoint
   */
  public async analyzeProperty(
    url: string,
    source: PropertySource,
    selectedTools: AnalysisToolType[],
    _onProgress?: (progress: { [tool: string]: number }) => void
  ): Promise<PropertyAnalysisResponse> {
    // Validate inputs
    if (!url || !url.trim()) {
      throw new PropertyAnalysisError('Property URL is required', 'INVALID_URL');
    }

    if (!this.validatePropertyUrl(url, source)) {
      throw new PropertyAnalysisError(
        `Invalid ${source} URL format`,
        'INVALID_URL_FORMAT'
      );
    }

    if (!selectedTools || selectedTools.length === 0) {
      throw new PropertyAnalysisError('At least one analysis tool must be selected', 'NO_TOOLS_SELECTED');
    }

    // Check authentication
    const jwt = getJwtFromCookie();
    if (!jwt) {
      throw new PropertyAnalysisError('Authentication required', 'AUTH_REQUIRED');
    }

    // Get user email from JWT token
    const userEmail = getUserEmailFromJwt();
    if (!userEmail) {
      throw new PropertyAnalysisError('User email required', 'EMAIL_REQUIRED');
    }

    // Create a unique key for deduplication based on URL and tools
    const deduplicationKey = `${url}_${[...selectedTools].sort().join(',')}_${userEmail}`;
    
    // Check if a similar request is already in progress
    if (this.requestInProgress.has(deduplicationKey)) {
      throw new PropertyAnalysisError(
        'A similar analysis is already in progress. Please wait for it to complete.',
        'DUPLICATE_REQUEST'
      );
    }

    // Add fixed delay to ensure server readiness and avoid concurrency issues
    const serverWaitDelay = 2000; // Fixed 2 second delay for server stability
    console.log(`Adding ${serverWaitDelay}ms delay before analysis request for server stability...`);
    await this.delay(serverWaitDelay);

    // Generate request ID for tracking
    const requestId = `analysis_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const abortController = new AbortController();
    this.activeRequests.set(requestId, abortController);
    this.requestInProgress.add(deduplicationKey);

    try {
      // Create request payload
      const analysisRequest: PropertyAnalysisRequest = {
        url: url.trim(),
        source,
        email: userEmail,
        config: this.createAnalysisConfig(selectedTools)
      };

      console.log(`Starting property analysis for ${url} with tools:`, selectedTools);

      console.log('Making property analysis request with payload:', analysisRequest);

      // Make the API request with retry logic
      const result = await this.makeRequestWithRetry(
        () => this.apiService.post<PropertyAnalysisResponse['data']>(
          '/analysis/complete-analysis',
          analysisRequest,
          {
            headers: {
              'Content-Type': 'application/json'
            },
            timeout: this.config.timeout,
            signal: abortController.signal
          }
        ),
        'analyze property',
        requestId
      );

      console.log(`Property analysis completed for ${url}`, result);

      return {
        success: true,
        data: result,
        processingTime: result.processingTime,
        timestamp: result.timestamp
      };

    } catch (error: any) {
      console.error(`Property analysis failed for ${url}:`, error);
      throw this.handleApiError(error, 'analyze property');
    } finally {
      // Clean up active request and deduplication
      this.activeRequests.delete(requestId);
      this.requestInProgress.delete(deduplicationKey);
    }
  }

  /**
   * Cancels an active analysis request
   */
  public cancelAnalysis(requestId?: string): void {
    if (requestId && this.activeRequests.has(requestId)) {
      const controller = this.activeRequests.get(requestId);
      controller?.abort();
      this.activeRequests.delete(requestId);
      console.log(`Cancelled analysis request: ${requestId}`);
    } else {
      // Cancel all active requests
      this.activeRequests.forEach((controller, id) => {
        controller.abort();
        console.log(`Cancelled analysis request: ${id}`);
      });
      this.activeRequests.clear();
      this.requestInProgress.clear();
    }
  }

  /**
   * Gets the status of all active analysis requests
   */
  public getActiveRequestIds(): string[] {
    return Array.from(this.activeRequests.keys());
  }

  /**
   * Checks if a specific request is still active
   */
  public isRequestActive(requestId: string): boolean {
    return this.activeRequests.has(requestId);
  }

  /**
   * Helper method to analyze all available tools for a property
   */
  public async analyzeAllTools(
    url: string,
    source: PropertySource,
    onProgress?: (progress: { [tool: string]: number }) => void
  ): Promise<PropertyAnalysisResponse> {
    const allTools: AnalysisToolType[] = [
      'propertyParser',
      'cma',
      'neighborhood',
      'pricing',
      'negotiation',
      'floorPlan',
      'listingDescription',
      'clientConsultation',
      'leadQualification',
      'transactionManagement',
      'preTourPrep',
      'clientMatch',
      'buyerSentiment',
      'agentOrchestrator'
    ];

    return this.analyzeProperty(url, source, allTools, onProgress);
  }

  /**
   * Updates service configuration
   */
  public updateConfig(newConfig: Partial<PropertyServiceConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Gets current service configuration
   */
  public getConfig(): PropertyServiceConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const propertyService = new PropertyService();

// Export class for custom instances
export default PropertyService;